import { ApolloClient, isApolloError, NormalizedCacheObject } from '@apollo/client';
import { addBreadcrumb } from '@sentry/react';

import {
  GetAccessTokenByRefreshTokenDocument,
  GetAccessTokenByRefreshTokenMutation,
  GetAccessTokenByRefreshTokenMutationVariables,
} from '@ocx/graphql';
import { getIsRudderStackEnabledFromApolloCache, getRudderStackInstance } from '@ocx/data-analytics';

import { accessTokenVar, isLoggedInVar, refreshTokenVar } from './authReactiveVariables';
import { logInDev } from '../../lib/logger/logger';

export class AuthService {
  private apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

  public injectNetworkClient(client: ApolloClient<NormalizedCacheObject>) {
    this.apolloClient = client;
  }

  public setTokens = (accessToken: string, refreshToken: string): void => {
    if (!accessToken) {
      // WARN: this is a temporary solution to fix double magic links issue
      isLoggedInVar(false);
    }
    accessTokenVar(accessToken);
    refreshTokenVar(refreshToken);
  };

  public async refreshTokens(): Promise<void> {
    const accessToken = accessTokenVar();
    if (!accessToken || !this.apolloClient) {
      await this.clearSession();
      return;
    }

    const variables: GetAccessTokenByRefreshTokenMutationVariables = {
      input: {
        accessToken: accessTokenVar(),
        refreshToken: refreshTokenVar(),
      },
    };
    const isRudderStackEnabled = getIsRudderStackEnabledFromApolloCache(this.apolloClient);
    const { triggerEvent } = getRudderStackInstance(isRudderStackEnabled);

    try {
      const response = await this.apolloClient.mutate<GetAccessTokenByRefreshTokenMutation>({
        variables,
        mutation: GetAccessTokenByRefreshTokenDocument,
      });

      // Impossible scenario, handling types
      if (!response.data?.getAccessTokenByRefreshToken) {
        this.setTokens('', '');
        triggerEvent('auth_session_refresh_failed');
        return;
      }

      const { accessToken, refreshToken } = response.data.getAccessTokenByRefreshToken;
      this.setTokens(accessToken, refreshToken);
      triggerEvent('auth_session_refreshed');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      this.setTokens('', '');
      triggerEvent('auth_session_refresh_failed');

      if (!isApolloError(e)) {
        addBreadcrumb({
          message: 'Failed to refresh auth session: Unexpected non-Apollo error occurred',
          data: { error: e },
        });
        triggerEvent('auth_session_refresh_token_not_apollo_error');
        return;
      }

      if (e.graphQLErrors?.length && e.graphQLErrors[0].message === 'Refresh Token has expired') {
        triggerEvent('auth_session_refresh_token_expired');
        return;
      }

      if (e.graphQLErrors?.length && e.graphQLErrors[0].message === 'Invalid Refresh Token') {
        triggerEvent('auth_session_refresh_token_invalid');
      }
    }
  }

  public async clearSession(): Promise<void> {
    addBreadcrumb({
      category: 'logger_auth',
      data: {
        'safe-message': `setTokens('', '')`,
      },
      level: 'info',
    });
    this.setTokens('', '');

    // https://www.apollographql.com/docs/react/networking/authentication/#reset-store-on-logout
    try {
      addBreadcrumb({
        category: 'logger_auth',
        data: {
          'safe-message': `await this.apolloClient?.clearStore();`,
        },
        level: 'info',
      });
      // https://www.apollographql.com/docs/react/api/core/ApolloClient/#resetStore
      await this.apolloClient?.resetStore();
    } catch (e) {
      addBreadcrumb({
        category: 'logger_auth',
        data: {
          'safe-message': `await this.apolloClient?.resetStore() throws an exception`,
        },
        level: 'error',
      });
      logInDev(e);
    }
  }

  public getAccessToken(): string {
    return accessTokenVar();
  }
}
